(() => {
  'use strict';

  const comboStorageKey = 'basictex:esenciales-combo:v1';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const parseJSON = (node) => {
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (error) { return null; }
  };

  const readCombo = () => {
    try {
      const state = JSON.parse(window.localStorage.getItem(comboStorageKey) || 'null');
      if (!state || ![3, 6, 12].includes(Number(state.target)) || !Array.isArray(state.items)) return { target: 0, count: 0, items: [] };
      return { target: Number(state.target), count: state.items.length, items: state.items };
    } catch (error) {
      return { target: 0, count: 0, items: [] };
    }
  };

  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0
      }).format(value).replace('COP', '$').replace(/\s/g, '');
    } catch (error) {
      return `$${Math.round(value).toLocaleString('es-CO')}`;
    }
  };

  class EssentialsProduct {
    constructor(root) {
      this.root = root;
      this.data = parseJSON(root.querySelector('[data-btep-product-json]')) || { variants: [] };
      this.form = root.querySelector('form[data-type="add-to-cart-form"]') || root.querySelector('form[action*="/cart/add"]');
      this.variantField = this.form?.querySelector('[name="id"]') || null;
      this.atc = this.form?.querySelector('[data-atc-form]') || this.form?.querySelector('[name="add"]') || null;
      this.atcLabel = this.atc?.querySelector('.t4s-btn-atc_text') || this.atc?.querySelector('span') || null;
      this.singleMode = root.querySelector('[data-btep-single-mode]');
      this.packTiers = Array.from(root.querySelectorAll('[data-bte-product-tier]'));
      this.status = root.querySelector('[data-bte-product-status]');
      this.price = root.querySelector('[data-btep-price]');
      this.compare = root.querySelector('[data-btep-compare]');
      this.sticky = root.querySelector('[data-btep-sticky]');
      this.stickyPrice = root.querySelector('[data-btep-sticky-price]');
      this.stickyAction = root.querySelector('[data-btep-sticky-action]');
      this.gallery = root.querySelector('[data-btb-gallery]');
      this.stage = root.querySelector('[data-btb-gallery-track]');
      this.slides = Array.from(root.querySelectorAll('[data-btb-gallery-slide]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-btb-thumb]'));
      this.currentIndex = 0;
      this.combo = readCombo();
      this.explicitSingle = false;
      this.syncTimer = null;
      this.bind();
      this.syncVariant({ moveGallery: false });
      this.syncPurchaseMode();
      this.syncGallery(0, false);
      this.observeSticky();
      this.root.dataset.btepReady = 'true';
    }

    bind() {
      this.singleMode?.addEventListener('click', () => {
        this.explicitSingle = true;
        this.setPurchaseMode('single');
        if (this.status && this.combo.target) this.status.textContent = 'Tu combo sigue guardado. Puedes volver a él cuando quieras.';
      });

      this.packTiers.forEach((tier) => {
        tier.addEventListener('click', () => {
          this.explicitSingle = false;
          this.combo.target = Number(tier.dataset.target || 0);
          this.setPurchaseMode('combo');
        });
      });

      this.form?.addEventListener('submit', (event) => {
        if (!this.root.classList.contains('is-combo-mode')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.atc?.click();
      }, true);

      this.atc?.addEventListener('click', (event) => {
        if (!this.root.classList.contains('is-combo-mode')) return;
        event.preventDefault();
        event.stopImmediatePropagation();

        const variant = this.selectedVariant();
        if (!variant?.available) return;

        const comboSystem = window.BasicTexEssentialsCombo;
        if (comboSystem && typeof comboSystem.addCurrentProduct === 'function') {
          comboSystem.addCurrentProduct(this.atc);
          return;
        }

        if (this.status) this.status.textContent = 'Estamos preparando tu combo. Intenta de nuevo en un instante.';
      }, true);

      this.root.addEventListener('change', () => this.scheduleVariantSync(true));
      this.root.addEventListener('click', (event) => {
        if (event.target.closest('[data-swatch-item], [data-dropdown-off]')) this.scheduleVariantSync(true);
      });

      ['variant:change', 'variant:changed', 'product:variant-change', 't4s:variant:change'].forEach((name) => {
        document.addEventListener(name, () => this.scheduleVariantSync(true));
      });

      document.addEventListener('basictex:combo-ready', (event) => this.handleCombo(event.detail));
      document.addEventListener('basictex:combo-change', (event) => this.handleCombo(event.detail));

      this.stickyAction?.addEventListener('click', () => {
        if (!this.atc) return;
        const formTop = this.form?.getBoundingClientRect().top ?? 0;
        if (formTop > window.innerHeight || formTop > 100) {
          this.root.querySelector('[data-btep-offer]')?.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
        } else {
          this.atc.click();
        }
      });
    }

    handleCombo(detail) {
      this.combo = {
        target: Number(detail?.target || 0),
        count: Number(detail?.count ?? detail?.items?.length ?? 0),
        items: detail?.items || []
      };
      this.syncPurchaseMode();
    }

    syncPurchaseMode() {
      const mode = this.combo.target && !this.explicitSingle ? 'combo' : 'single';
      this.setPurchaseMode(mode);
    }

    setPurchaseMode(mode) {
      const isCombo = mode === 'combo' && Boolean(this.combo.target);
      this.root.classList.toggle('is-combo-mode', isCombo);
      this.singleMode?.classList.toggle('is-selected', !isCombo);
      this.singleMode?.setAttribute('aria-checked', String(!isCombo));
      this.packTiers.forEach((tier) => {
        const selected = isCombo && Number(tier.dataset.target) === Number(this.combo.target);
        tier.classList.toggle('is-selected', selected);
        tier.setAttribute('aria-checked', String(selected));
      });

      if (!this.atc) return;
      if (isCombo) {
        this.atc.type = 'button';
        this.atc.removeAttribute('name');
        this.atc.removeAttribute('data-atc-form');
        this.atc.setAttribute('data-bte-add-current', '');
      } else {
        this.atc.type = 'submit';
        this.atc.name = 'add';
        this.atc.setAttribute('data-atc-form', '');
        this.atc.removeAttribute('data-bte-add-current');
      }
      this.renderAtc();
    }

    selectedVariant() {
      const id = String(this.variantField?.value || '');
      return this.data.variants.find((variant) => String(variant.id) === id) || this.data.variants[0] || null;
    }

    syncVariant(options = {}) {
      const variant = this.selectedVariant();
      if (!variant) return;
      if (this.price) this.price.textContent = formatMoney(variant.price);
      if (this.stickyPrice) this.stickyPrice.textContent = formatMoney(variant.price);
      if (this.compare) {
        const visible = Number(variant.compareAtPrice || 0) > Number(variant.price || 0);
        this.compare.hidden = !visible;
        if (visible) this.compare.textContent = formatMoney(variant.compareAtPrice);
      }
      if (options.moveGallery !== false && variant.featuredMediaId) {
        const index = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(variant.featuredMediaId));
        if (index >= 0) this.goTo(index);
      }
      this.renderAtc();
    }

    scheduleVariantSync(moveGallery) {
      window.clearTimeout(this.syncTimer);
      let attempts = 0;
      const sync = () => {
        this.syncVariant({ moveGallery });
        attempts += 1;
        if (attempts < 5) this.syncTimer = window.setTimeout(sync, 70);
      };
      this.syncTimer = window.setTimeout(sync, 0);
    }

    renderAtc() {
      if (!this.atc || !this.atcLabel) return;
      const variant = this.selectedVariant();
      const unavailable = !variant || !variant.available;
      const isCombo = this.root.classList.contains('is-combo-mode');
      this.atc.disabled = unavailable;
      this.atc.setAttribute('aria-disabled', String(unavailable));
      if (isCombo) this.atc.removeAttribute('data-atc-form');
      else if (!unavailable) this.atc.setAttribute('data-atc-form', '');
      if (unavailable) {
        this.atcLabel.textContent = 'AGOTADO · ELIGE OTRA VARIANTE';
      } else if (isCombo) {
        const count = Number(this.combo.count || 0);
        const target = Number(this.combo.target || 0);
        this.atcLabel.textContent = count >= target
          ? 'REVISAR COMBO COMPLETO'
          : `AGREGAR AL COMBO · ${count}/${target}`;
      } else {
        this.atcLabel.textContent = `AGREGAR AL CARRITO · ${formatMoney(variant.price)}`;
      }
      if (this.stickyAction) this.stickyAction.textContent = this.atcLabel.textContent;
    }

    goTo(index) {
      if (!this.slides.length) return;
      const next = Math.max(0, Math.min(index, this.slides.length - 1));
      if (this.gallery?.dataset.btbReady === 'true') {
        this.gallery.dispatchEvent(new CustomEvent('basictex:gallery-go-to', { detail: { index: next } }));
      } else if (this.stage) {
        this.stage.scrollTo({ left: this.slides[next].offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        this.syncGallery(next, true);
      }
    }

    syncGallery(index, scrollThumb = true) {
      if (!this.slides.length) return;
      this.currentIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const output = this.gallery?.querySelector('[data-btb-gallery-counter]');
      if (output) output.textContent = `${this.currentIndex + 1} / ${this.slides.length}`;
      this.thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === this.currentIndex;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', String(active));
      });
      if (scrollThumb) this.thumbs[this.currentIndex]?.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      this.slides.forEach((slide, slideIndex) => {
        if (slideIndex !== this.currentIndex) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    }

    observeSticky() {
      if (!this.sticky || !this.atc || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(([entry]) => {
        this.sticky.hidden = entry.isIntersecting;
      }, { threshold: .2 });
      observer.observe(this.atc);
    }
  }

  const init = (root = document) => {
    root.querySelectorAll('[data-btep-product]:not([data-btep-ready])').forEach((node) => new EssentialsProduct(node));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
